// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { GetAccountApiParameter } from "@insite/client-framework/Services/AccountService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import setUserFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TextField from "@sc/MobiusOverrides/TextField/TextField";
import { xMask } from "@sc/XDL/Utilities";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Change adding a tooltip to user role and approver like in User Adminstration Page
const enum fields {
    assignApproverHelpMessage = "assignApproverHelpMessage",
    roleInformation = "roleInformation",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.assignApproverHelpMessage]: string;
        [fields.roleInformation]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
});

const mapDispatchToProps = {
    setUserFields,
    loadAccount,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface UserSetupSettingsStyles {
    title?: TypographyPresentationProps;
    fieldset?: InjectableCss;
    wrapper?: InjectableCss;
    userRoleSelect?: SelectPresentationProps;
    approverSelect?: SelectPresentationProps;
    approverDynamicDropdown?: DynamicDropdownPresentationProps;
    // XNG-Change
    roleSelectHelpIcon?: IconPresentationProps;
    roleSelectHelpModal?: ModalPresentationProps;
    approverSelectTooltip?: TooltipPresentationProps;
}

export const userSetupSettingsStyles: UserSetupSettingsStyles = {
    title: { variant: "h5" },
    fieldset: {
        css: css`
            margin: 0;
            padding: 0;
            border: 0;
        `,
    },
    wrapper: {
        css: css`
            padding-bottom: 15px;
            font-weight: 400;
        `,
    },
};

const styles = userSetupSettingsStyles;
const StyledFieldSet = getStyledWrapper("fieldset");

const UserSetupSettings = ({ editingUser, setUserFields, loadAccount, fields }: Props) => {
    if (!editingUser) {
        return null;
    }
    const [options, setOptions] = useState<OptionObject[]>([]);
    // XNG-Change
    const [roleInformationModalIsOpen, setRoleInformationModalIsOpen] = useState(false);
    const [orderApprovalThreshold, setOrderApprovalThreshold] = useState<string>("");

    useEffect(() => {
        if (editingUser && options.length < 1) {
            setApproverOptions("");
        }
        loadAccount({
            accountId: editingUser.id,
            numberOfApprovers: 20,
            onComplete: result => {
                const orderApprovalThreshold = result?.apiResult?.properties?.orderApprovalThreshold;
                if (orderApprovalThreshold) {
                    setOrderApprovalThreshold(orderApprovalThreshold);
                }
            },
        });
    }, [editingUser.id]);

    const userRoleChangeHandler: SelectProps["onChange"] = event => {
        setUserFields({ role: event.target.value });
        setUserFields({ approver: "" });
    };

    const approverChangeHandler = (value?: string) => {
        const approverValue = value || "";
        setUserFields({ approver: approverValue });
    };

    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const filterValue = event.currentTarget.value;
        debouncedSearchApprovers(filterValue);
    };

    const debouncedSearchApprovers = debounce((filterValue: string) => {
        setApproverOptions(filterValue);
    }, 300);

    const setApproverOptions = (filterValue: string) => {
        const currentAccountId = editingUser.id;
        if (!currentAccountId) {
            return;
        }

        let approverOptions: OptionObject[] = [{ optionText: translate("Select an Approver"), optionValue: "" }];
        const parameter = {
            accountId: currentAccountId,
            numberOfApprovers: 20,
        } as GetAccountApiParameter;

        if (filterValue !== "") {
            parameter.approversUsernameFilter = filterValue;
        }

        loadAccount({
            ...parameter,

            onComplete: result => {
                if (!result?.apiResult?.availableApprovers) {
                    return;
                }

                approverOptions = approverOptions.concat(
                    result.apiResult.availableApprovers.map(o => ({ optionText: o, optionValue: o })),
                );

                setOptions(approverOptions);
            },
        });
    };

    // xng-change
    const handleUpdateOrderApprovalThreshold = (value: string) => {
        setOrderApprovalThreshold(value);
        setUserFields({ orderApprovalThreshold: value } as any);
    };

    // XNG-Change
    const roleSelectLabel = fields.roleInformation ? (
        <>
            {translate("Assign Role")}
            <Icon {...styles.roleSelectHelpIcon} onClick={() => setRoleInformationModalIsOpen(true)} />
            <Modal
                {...styles.roleSelectHelpModal}
                headline={translate("User Roles")}
                isOpen={roleInformationModalIsOpen}
                handleClose={() => setRoleInformationModalIsOpen(false)}
            >
                {parse(fields.roleInformation, parserOptions)}
            </Modal>
        </>
    ) : (
        translate("Assign Role")
    );

    const approverLabel = fields.assignApproverHelpMessage ? (
        <>
            {translate("Assign Approver")}
            <Tooltip {...styles.approverSelectTooltip} text={fields.assignApproverHelpMessage} />
        </>
    ) : (
        translate("Assign Approver")
    );

    return (
        <>
            <Typography as="h2" {...styles.title} data-test-selector="userSetupSetting_title">
                {translate("Settings")}
            </Typography>
            <StyledFieldSet {...styles.fieldset}>
                <StyledWrapper {...styles.wrapper}>
                    <Select
                        {...styles.userRoleSelect}
                        label={roleSelectLabel}
                        value={editingUser.role}
                        onChange={userRoleChangeHandler}
                        data-test-selector="userSetupSetting_userRole"
                    >
                        <option value="">{translate("Select User Role")}</option>
                        {editingUser.availableRoles?.map(role => (
                            <option key={role} value={role}>
                                {(editingUser.availableRolesTranslated &&
                                    editingUser.availableRolesTranslated[role.toLowerCase()]) ||
                                    role}
                            </option>
                        ))}
                    </Select>
                </StyledWrapper>
                <StyledWrapper {...styles.wrapper}>
                    <DynamicDropdown
                        {...styles.approverDynamicDropdown}
                        label={approverLabel}
                        onInputChange={onInputChanged}
                        onSelectionChange={approverChangeHandler}
                        selected={editingUser.approver}
                        options={options}
                        data-test-selector="userSetupSetting_assignApprover"
                        placeholder={translate("Select an Approver")}
                    />
                </StyledWrapper>
                {editingUser.role === "Buyer3A" && (
                    <StyledWrapper {...styles.wrapper}>
                        <TextField
                            label={
                                <>
                                    {translate("Amount Needing Approval")}
                                    <Tooltip
                                        text={siteMessage("Custom_SC_Amount_Needing_Approval_Help_Message") as string}
                                    />
                                </>
                            }
                            placeholder={translate("Amount Needing Approval")}
                            value={orderApprovalThreshold}
                            onChange={event => {
                                const value = xMask.decimal(event.target.value);
                                handleUpdateOrderApprovalThreshold(value);
                            }}
                            type="number"
                            min={0}
                        />
                    </StyledWrapper>
                )}
            </StyledFieldSet>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupSettings),
    definition: {
        group: "User Setup",
        displayName: "Settings",
        allowedContexts: ["UserSetupPage"],
        fieldDefinitions: [
            {
                name: fields.assignApproverHelpMessage,
                displayName: "Assign Approver Help Message",
                editorTemplate: "TextField",
                defaultValue: "Requisitioner, Buyer Level 1 require approver assignment.",
                fieldType: "Translatable",
            },
            {
                name: fields.roleInformation,
                displayName: "Role Information",
                editorTemplate: "RichTextField",
                defaultValue: `
                <div>
                    <h5>User Administrator: </h5>
                    <ul>
                        <li>Full access to My Account. </li>
                        <li>Can order over budget.</li>
                        <li>Can see invoices and orders for users that User Administrator is assigned to.</li>
                        <li>Default approver if none is assigned to a user.</li>
                        <li>Can approve requisitions.</li>
                    </ul>
                </div>
                <div>
                    <h5>Buyer Level 3:</h5>
                    <ul>
                        <li>Can order over budget without approval.</li>
                        <li>Can see orders and invoices.</li>
                        <li>Cannot access User Administration or Budget Management. </li>
                        <li>Can approve requisitions. </li>
                    </ul>
                </div>
                <div>
                    <h5>Buyer Level 2:</h5>
                    <ul>
                        <li>Over budget orders require approval.</li>
                        <li>Can see orders.</li>
                        <li>Cannot see invoices.</li>
                        <li>Cannot access User Administration, Budget Management, or Requisition Approval. </li>
                    </ul>
                </div>
                <div>
                    <h5>Buyer Level 1:</h5>
                    <ul>
                        <li>Cannot be assigned as an approver.</li>
                        <li>All orders require approval.</li>
                        <li>Can see orders.</li>
                        <li>Cannot see invoices.</li>
                        <li>Cannot access User Administration, Budget Management, or Requisition Approval. </li>
                    </ul>
                </div>
                <div>
                    <h5>Requisitioner:</h5>
                    <ul>
                        <li>Can only place requisition requests.</li>
                        <li>Cannot place orders.</li>
                        <li>Cannot access Order History, Invoice History, Order Approval, User Administration, Budget Management, or Requisition Approval. </li>
                    </ul>
                </div>`,
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
